import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { BookOpen, Award, PlayCircle, Clock, Trophy } from 'lucide-react';
import CourseCard from '../components/CourseCard';

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [enrolled, setEnrolled] = useState([]);
  const [all, setAll] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.myEnrollments().then(setEnrolled).catch(() => {});
    api.courses().then(setAll).catch(() => {});
  }, [user]);

  if (loading) return <div className="pt-24 text-center">Loading…</div>;
  if (!user) {
    return (
      <div className="pt-24 text-center py-20">
        <h1 className="text-3xl font-bold mb-3">Please sign in</h1>
        <Link to="/login"><Button>Sign in</Button></Link>
      </div>
    );
  }

  const enrolledIds = new Set(enrolled.map(e => e.id));
  const recommended = all.filter(c => !enrolledIds.has(c.id)).slice(0, 4);
  const totalHours = enrolled.reduce((s, c) => s + (c.modules || []).reduce((a, m) => a + m.hours, 0), 0);

  return (
    <div className="pt-16 min-h-screen bg-purple-50/30">
      <section className="bg-gradient-to-br from-[#6b47dc] via-[#7c3aed] to-[#ff5722] text-white py-12">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <p className="text-sm opacity-80">Welcome back,</p>
          <h1 className="text-3xl md:text-4xl font-extrabold mb-6">{user.name}</h1>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            {[
              { icon: BookOpen, label: 'Enrolled', value: enrolled.length },
              { icon: Clock, label: 'Hours', value: totalHours },
              { icon: Award, label: 'Certificates', value: 0 },
              { icon: Trophy, label: 'Rank', value: 'Bronze' },
            ].map((s, i) => (
              <div key={i} className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/10">
                <s.icon size={20} className="mb-2" />
                <div className="text-2xl font-extrabold">{s.value}</div>
                <div className="text-xs opacity-80 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
        <h2 className="text-2xl font-extrabold mb-5">My learning</h2>
        {enrolled.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
            <BookOpen size={48} className="mx-auto text-[#6b47dc] mb-3" />
            <h3 className="font-bold text-lg mb-2">No enrollments yet</h3>
            <p className="text-gray-500 mb-4">Browse courses and start your journey.</p>
            <Link to="/courses"><Button className="bg-[#6b47dc] hover:bg-[#5a3ac0]">Browse courses</Button></Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {enrolled.map(c => (
              <div key={c.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative aspect-video overflow-hidden">
                  <img src={c.thumbnail} alt={c.title} className="w-full h-full object-cover" />
                  <div className={`absolute inset-0 bg-gradient-to-tr ${c.color} opacity-40`} />
                  <div className="absolute inset-0 grid place-items-center">
                    <PlayCircle size={44} className="text-white drop-shadow-lg" />
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-gray-900">{c.title}</h3>
                  <p className="text-xs text-gray-500 mt-1">By {c.instructor}</p>
                  <div className="mt-3">
                    <div className="flex justify-between text-xs mb-1"><span>Progress</span><span className="font-bold text-[#6b47dc]">12%</span></div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#6b47dc] to-[#ff5722] rounded-full" style={{ width: '12%' }} /></div>
                  </div>
                  <Link to={`/course/${c.id}`}><Button className="mt-4 w-full bg-[#6b47dc] hover:bg-[#5a3ac0]">Continue learning</Button></Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {recommended.length > 0 && (
          <>
            <h2 className="text-2xl font-extrabold mt-14 mb-5">Recommended for you</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {recommended.map(c => <CourseCard key={c.id} course={c} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
